import { APP_SCORING_MAX_SCORE, SIMULATION_LABELS } from '@/constants';
import { capitalize, orderBy } from 'lodash';
import { cn, formatDate } from '@/utils';
import { ISimulationScore, TSimulationType } from '@/types';
import { RoleplayCardHistoryItem } from './RoleplayCardHistoryItem';
import { roleplayCardHistoryStyles } from '../RoleplayCard.styles';
import { RoleplayCardProps } from '../RoleplayCard';
import { SkeletonLoader } from '@/components/Loaders';
import { SkillLevelScale } from '@/components/Score';
import { Tag } from '@/components/Tags';
import { TextContent } from '@/components/Text';
import React, { useMemo } from 'react';
import {
  getScorePass,
  getSkillLevel,
  selectDisplayScoreValue,
  selectSkillLevelInputValue,
} from '@/domains/scoring';

export interface RoleplayCardHistoryProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Loading state to indicate whether the history data is still being fetched or processed.
   */
  isLoading?: boolean;

  /**
   * Array of roleplay session history items.
   */
  scores: ISimulationScore[];

  /**
   * Handler for when the "Share with manager" button is clicked.
   */
  onShareWithManager?: RoleplayCardProps['onShareWithManager'];

  /**
   * Handler for when the "View scorecard" button is clicked.
   */
  onViewScoreCard?: RoleplayCardProps['onViewScoreCard'];

  /**
   * Maximum height for the scrollable history list (defaults to 165px).
   */
  maxHeight?: number;

  /**
   * Type of simulation (role-play, practice, certification) to adjust display formatting if needed.
   */
  showSkillLevelAs: RoleplayCardProps['showSkillLevelAs'];

  /**
   * Type of simulation (role-play, practice, certification) to adjust display formatting if needed.
   */
  simulationType?: TSimulationType;

  /**
   * Title for the history section.
   */
  title?: string;

  /**
   * User ID for sharing functionality.
   */
  userId?: string;
}

/**
 * RoleplayCardHistory displays a list of previous roleplay session history
 * with scores, dates, and action buttons for sharing and viewing scorecards.
 */
export const RoleplayCardHistory: React.FC<RoleplayCardHistoryProps> = ({
  className,
  isLoading = false,
  maxHeight = 165,
  onShareWithManager,
  onViewScoreCard,
  scores = [],
  showSkillLevelAs,
  simulationType,
  title,
  userId,
  ...props
}) => {
  /**
   * Sort scores by date completed in descending order to show the most recent scores first.
   * This ensures that the latest score appears at the top of the history list.
   * The sorting is memoized to avoid unnecessary re-sorting on every render.
   * The dateCompleted field is expected to be a string or Date that can be parsed by formatDate.
   * If dateCompleted is missing or invalid, date then is 0 (epoch) and appear at the end.
   *
   * If isLoading is true, we return a placeholder array of scores to render skeleton loaders
   * in the UI.  Each placeholder score has a unique id and uuid to satisfy
   * React's key requirements.
   */
  const sortedScores = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, i) => {
        const skeletonLoadingScore: ISimulationScore = {
          categories: [],
          dateCompleted: new Date().toISOString(),
          id: i,
          simulationId: `loading-score-${i}-id`,
          uuid: `loading-score-${i}-uuid`,
        };

        return skeletonLoadingScore;
      });
    }

    return orderBy(
      scores,
      (s) => {
        const date = new Date(s.dateCompleted);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      },
      'desc',
    );
  }, [isLoading, scores]);

  /**
   * Determine the title based on the simulation type, with a default for role-play
   */
  const historyTitle = useMemo(() => {
    if (title) return title;
    switch (simulationType) {
      case 'practice':
        return SIMULATION_LABELS.PRACTICE_SCORES;
      case 'certification':
        return SIMULATION_LABELS.CERTIFICATION_SCORES;
      default:
        return SIMULATION_LABELS.PREVIOUS_ROLEPLAY_SCORES;
    }
  }, [simulationType, title]);

  /**
   * Determine skill level based on the most recent score
   */
  const latestSkillLevel = useMemo(() => {
    if (!sortedScores || sortedScores.length === 0) return null;
    const scoreNum = selectSkillLevelInputValue({
      score: sortedScores[0]?.score,
      scorePercentage: sortedScores[0]?.scorePercentage,
    });

    return getSkillLevel(scoreNum, sortedScores[0]?.runtimeRatingCutoffs);
  }, [sortedScores]);

  /**
   * Determine the highest skill level across all scores to display in the history list.
   * This iterates through all scores, extracts the relevant score value based on the app's scoring format,
   * and keeps track of the highest score to determine the corresponding skill level.
   * If there are no scores or all scores are invalid, it returns null.
   *
   * Note: Uses the runtime rating cutoffs from the highest score entry found.
   */
  const highestSkillLevel = useMemo(() => {
    if (!sortedScores || sortedScores.length === 0) return null;
    let highestScore = 0;
    let highestScoreEntry = sortedScores[0];

    sortedScores.forEach((score) => {
      const scoreNum = selectSkillLevelInputValue({
        fallback: 0,
        score: score.score,
        scorePercentage: score.scorePercentage,
      });

      if (scoreNum > highestScore) {
        highestScore = scoreNum;
        highestScoreEntry = score;
      }
    });

    return getSkillLevel(highestScore, highestScoreEntry?.runtimeRatingCutoffs);
  }, [sortedScores]);

  /**
   * Determine which skill level to display based on the SKILL_TO_DISPLAY constant.
   * If SKILL_TO_DISPLAY is set to 'highest', it will show the highest skill level achieved across all scores.
   * If SKILL_TO_DISPLAY is set to 'latest', it will show the skill level of the most recent score.
   * This allows for flexibility in how the skill level is presented in the history list.
   */
  const skillToDisplay = showSkillLevelAs === 'highest' ? highestSkillLevel : latestSkillLevel;

  /**
   * Helper function to extract the appropriate score value based on the app's scoring format.
   * This ensures that the correct score representation is displayed in the history list.
   * For percentage format, it uses scorePercentage; for other formats, it uses score.
   */
  const getScore = (score: ISimulationScore | undefined) => {
    return selectDisplayScoreValue({
      fallback: 0,
      score: score?.score,
      scorePercentage: score?.scorePercentage,
    });
  };

  return (
    <div className={cn(roleplayCardHistoryStyles.base, className)} {...props}>
      {/* Title */}
      <div className="w- flex w-full flex-row flex-nowrap items-center justify-between">
        <TextContent
          dataContent="Roleplay History Title"
          isLoading={isLoading}
          label={historyTitle}
          level={3}
          loadingClassName="w-56 xl:w-96 h-[1.5rem]"
          testId="roleplay-history-title"
          type="headline"
        />

        {skillToDisplay && simulationType === 'role-play' && (
          <>
            <div className="hidden xs:block 2xl:hidden">
              {isLoading ? (
                <SkeletonLoader height="sm" width="xs" rounded="md" />
              ) : (
                <Tag label={capitalize(skillToDisplay)} variant={skillToDisplay} />
              )}
            </div>
            <div className="ml-auto hidden w-full max-w-[335px] pl-4 2xl:block">
              {isLoading ? (
                <SkeletonLoader height="sm" width="2xl" rounded="md" style={{ height: 38 }} />
              ) : (
                <SkillLevelScale level={skillToDisplay} size="small" />
              )}
            </div>
          </>
        )}
      </div>

      {/* History List */}
      <div
        className={roleplayCardHistoryStyles.listContainer}
        style={{ maxHeight: `${maxHeight}px` }}
      >
        {sortedScores.map((score, index) => (
          <div
            className={roleplayCardHistoryStyles.listItem}
            key={`roleplay-history-item-${score.uuid}`}
          >
            <RoleplayCardHistoryItem
              date={formatDate(score.dateCompleted, 'fullDate')}
              isLastItem={index === sortedScores.length - 1}
              isLoading={isLoading}
              onShareWithManager={onShareWithManager}
              onViewScoreCard={onViewScoreCard}
              pass={getScorePass(undefined, simulationType, score) || false}
              score={getScore(score)}
              scoreCardId={score.id}
              scoreId={score.uuid}
              scoreMax={score.scoreMax ?? APP_SCORING_MAX_SCORE}
              sessionId={score?.sessionId}
              sharedWithManager={score.sharedWithManager ?? false}
              simulationType={simulationType}
              time={formatDate(score.dateCompleted, 'time')}
              type={score?.type ?? 'numeric'}
              userId={userId}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

RoleplayCardHistory.displayName = 'RoleplayCardHistory';

export default RoleplayCardHistory;
